import "./EditAssessmentView.scss";
import React, { useEffect, useState } from "react";
import {
  AssignFormRequest,
  ClinicalFormInfo,
  MemberAnswerInfo,
  MemberFormInfo,
  MemberFormStatus,
  Problem,
  SaveAnswerRequest,
} from "clinical-forms-experience-client";
import { createClinicalFormsApi } from "../../api-helpers/ClinicalFormsApiHelper";
import { LoadingHelper } from "../../components/loader/LoadingHelper";
import ClinicalFormDetailsView from "../ClinicalForms/ClinicalFormDetailsView";
import { ClinicalFormsConstants } from "../ClinicalForms/ClinicalFormsConstants";
import AssessmentPGIs from "./AssessmentPGIs";
import { createPgiApi } from "../../api-helpers/PgiApiHelper";
import { PgiRequest } from "pgi-experience-client";
import { createUsersApi } from "../../api-helpers/UserApiHelper";
import { UsersDto } from "users-experience-client";
import { Button, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { formatDate, formatDateTime } from "../../helper/dateFormatter";
import { useNavigate } from "react-router-dom";
import { ToastConfig } from "../../components/Toaster";
import { useDispatch } from "react-redux";
import { setLastAssessmentDate } from "../../store/slices/lastAssessmentDate";
import useLoggedInAccount from "../../auth/LoggedInAccountInfo";
import {
  isAssessmentOverdue,
  getAssessmentOverdueStatus,
} from "../Assessment-v2/utils.assessment";

const TooltipWrapper = ({
  status,
  color,
  dueDays,
}: {
  status: string;
  color: string;
  dueDays?: null | number;
}) => {
  const displayValue = dueDays != null ? `${status}: ${dueDays}d` : `${status}`;
  return (
    <OverlayTrigger
      trigger={["hover", "focus"]}
      placement="right"
      overlay={<Tooltip>{displayValue}</Tooltip>}
    >
      <div className={`mt-1 dot-status status-${color}`}></div>
    </OverlayTrigger>
  );
};

interface Props {
  patientId: string;
  memberFormId?: string;
  onClosed?: () => void;
  handleAddPgiForm: (toasterConfig: ToastConfig) => Promise<void>;
  reloadGrid?: () => void;
  clinicalForms: ClinicalFormInfo[];
  offCanvasShowCallback?: (comp: string) => void;
  source?: 'appointment' | 'manual';
  fromAddAssessment?: boolean;
  pgiIds?: {
    problemId: string | undefined;
    goals: {
        goalId: string | undefined;
        interventions: (string | undefined)[];
    }[];
  }[];
}

const statusColorMap: Map<string, string> = new Map();
statusColorMap.set(ClinicalFormsConstants.incompleteStatus, "incomplete");
statusColorMap.set(ClinicalFormsConstants.draftStatus, "draft");
statusColorMap.set(ClinicalFormsConstants.submittedStatus, "submitted");
statusColorMap.set(ClinicalFormsConstants.dueSoonStatus, "due-soon");
statusColorMap.set(ClinicalFormsConstants.overdueStatus, "overdue");
statusColorMap.set(ClinicalFormsConstants.dueTodayStatus, "due-today");

const displayStatusMap: Map<string, string> = new Map();
displayStatusMap.set(ClinicalFormsConstants.incompleteStatus, "Incomplete");
displayStatusMap.set(ClinicalFormsConstants.draftStatus, "Draft");
displayStatusMap.set(ClinicalFormsConstants.submittedStatus, "Completed");
displayStatusMap.set(ClinicalFormsConstants.dueSoonStatus, "Due Soon");
displayStatusMap.set(ClinicalFormsConstants.overdueStatus, "Due");
displayStatusMap.set(ClinicalFormsConstants.dueTodayStatus, "Due Today");

const EditAssessmentView: React.FC<Props> = ({
  patientId,
  memberFormId,
  onClosed,
  handleAddPgiForm,
  reloadGrid,
  clinicalForms,
  offCanvasShowCallback,
  source,
  fromAddAssessment = false,
  pgiIds
}: Props) => {
  const userApi = createUsersApi();
  const clinicalformsApi = createClinicalFormsApi();
  const pgiApi = createPgiApi();
  const { loggedInAccount } = useLoggedInAccount();
  const [user, setUser] = useState<UsersDto>();
  const [formStatuses, setFormStatuses] = useState<
    Map<string, MemberFormStatus>
  >(new Map());
  const [assessmentStatus, setAssessmentStatus] = useState<Map<string, { status: string ; dueDays?: number | null }>>(
    new Map()
  );
  const [memberForm, setMemberForm] = useState<MemberFormInfo | null>(null);

  const [answers, setAnswers] = useState<MemberAnswerInfo[]>();
  const [comments, setComments] = useState<string>();
  const [clinicalFormVersionId, setClinicalFormVersionId] = useState<string>(memberFormId!);

  const [allAnswersGiven, setAllAnswersGiven] = useState(false);

  const [selectedProblems, setSelectedProblems] = useState<Problem[]>([]);

  const [discardConfirmationVisible, showDiscardConfirmation] = useState(false);

  const [lastSaveTime, setLastSaveTime] = useState<Date>();

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const fetchPGIs = async () => {
    try{
      const { data } = await clinicalformsApi.clinicalFormsControllerGetPgisForMemberForm(
        memberFormId!
      );
      setSelectedProblems(data)
    } catch(error) {
      console.error('Error fetching PGIs', error);
    }
  }
  
  useEffect(() => {
    const loadAssessmentStatuses = async () => {
      try {
        const calculateStatusAndDueDays = (form: MemberFormInfo) => {
          if (form.status === ClinicalFormsConstants.draftStatus) {
            return { status: ClinicalFormsConstants.draftStatus, dueDays: null };
          }

          // Check if assessment is overdue using centralized utility
          const overdueStatus = getAssessmentOverdueStatus(form.submittedOn!);
          if (overdueStatus.isOverdue) {
            return {
              status: ClinicalFormsConstants.overdueStatus,
              dueDays: overdueStatus.daysSince - 90,
            };
          }

          // Calculate days until due (90 days from submission)
          const dueDate = new Date(form.submittedOn!);
          dueDate.setDate(dueDate.getDate() + 90);
          const daysUntilDue = Math.ceil(
            (dueDate.setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) /
            (1000 * 60 * 60 * 24)
          );

          if (daysUntilDue < 1)
            return { status: ClinicalFormsConstants.dueTodayStatus, dueDays: null };
          if (daysUntilDue <= 7)
            return { status: ClinicalFormsConstants.dueSoonStatus, dueDays: null };
          return { status: ClinicalFormsConstants.submittedStatus, dueDays: null };
        };  
        const [allAssessments, memberForms] = await Promise.all([
          clinicalformsApi.clinicalFormsControllerGetClinicalForms(ClinicalFormsConstants.assessmentType),
          clinicalformsApi.clinicalFormsControllerGetMemberForms(patientId, ClinicalFormsConstants.assessmentType),
        ]);

        // Filter out discarded forms and get latest version of each form
        const activeMemberForms: Map<string, MemberFormInfo> = memberForms.data
          .filter(form => form.status !== ClinicalFormsConstants.discardedStatus)
          .reduce((acc, form) => {
            if (!acc.has(form.formId) || new Date(form.createdOn) > new Date(acc.get(form.formId)!.createdOn)) {
              acc.set(form.formId, form);
            }
            return acc;
          }, new Map<string, MemberFormInfo>());

        // Create status map for all assessments
        const formStatuses = new Map(
          allAssessments.data
            .filter(assessment => !Array.from(activeMemberForms.values())
            .some(form => form.status === ClinicalFormsConstants.discardedStatus && form.formId === assessment.id))
            .map(assessment => {
              const memberForm = activeMemberForms.get(assessment.id);
              const status = memberForm 
                ? calculateStatusAndDueDays(memberForm)
                : { status: ClinicalFormsConstants.incompleteStatus, dueDays: null };
              return [assessment.id, status];
            })
        );
        setAssessmentStatus(formStatuses);
      } catch (error) {
        console.error("Failed to load assessment statuses", error);
      }
    };
    loadAssessmentStatuses();
  }, [memberForm]);
  useEffect(() => {
    fetchPGIs();
    const loadStatuses = async () => {
      try {
        const response = (
          await clinicalformsApi.clinicalFormsControllerGetMemberFormStatuses(
            patientId,
            ClinicalFormsConstants.assessmentType
          )
        ).data;
        const formStatuses = new Map<string, MemberFormStatus>();
        response.forEach((status) => formStatuses.set(status.formId, status));
        setFormStatuses(formStatuses);
      } finally {
      }
    };
    loadStatuses();
  }, []);

  useEffect(() => {
    if (!loggedInAccount) {
      return;
    }

    const loadUser = async () => {
      const userResponse = await userApi.usersControllerFindOne(
        loggedInAccount.username
      );
      setUser(userResponse.data);
    };

    loadUser();
  }, [loggedInAccount]);

  useEffect(() => {
    if (!clinicalFormVersionId) {
      return;
    }

    const loadClinicalForms = async () => {
      LoadingHelper.showLoader(".main");
      try {
        // Load member form
        const memberFormResponse = (
          await clinicalformsApi.clinicalFormsControllerGetMemberForm(
            clinicalFormVersionId
          )
        ).data;

        // Set current member form
        setMemberForm(memberFormResponse);
        setComments(memberFormResponse.comments ?? "");
      } finally {
        LoadingHelper.hideLoader(".main");
      }
    };
    loadClinicalForms();
  }, [memberFormId]);

  useEffect(() => {
    if (!memberForm) {
      return;
    }

    const loadAnswers = async () => {
      LoadingHelper.showLoader(".clinical-form-details");
      try {
        const answers = (
          await clinicalformsApi.clinicalFormsControllerGetMemberAnswers(
            memberForm.id
          )
        ).data;
        setAnswers(answers.formAnswers);
      } finally {
        LoadingHelper.hideLoader(".clinical-form-details");
      }
    };
    loadAnswers();
  }, [memberFormId, memberForm]);

  const onAnswerChange = async (
    questionId: string,
    answer: MemberAnswerInfo
  ) => {
    const request: SaveAnswerRequest = {
      answer: answer.answer,
      notes: answer.notes,
    };
    await clinicalformsApi.clinicalFormsControllerSaveMemberAnswer(
      request,
      memberForm!.id,
      questionId
    );

    setLastSaveTime(new Date());
  };

  async function onCommentsChange(value: string): Promise<void> {
    setComments(value);
    await clinicalformsApi.clinicalFormsControllerUpdateMemberFormComments(
      {
        value: value,
      },
      memberForm!.id
    );
    setLastSaveTime(new Date());
  }

  async function submitForm(): Promise<void> {
    LoadingHelper.showLoader(".main");
    try {
      await clinicalformsApi.clinicalFormsControllerUpdateMemberFormStatus(
        {
          value: ClinicalFormsConstants.submittedStatus,
        },
        memberForm!.id
      );

      const response =
        await clinicalformsApi.clinicalFormsControllerGetPgisForMemberForm(
          memberForm!.id
        );
      if (response.data.length > 0) {
        setSelectedProblems(response.data);
      } else {
        onClosed?.();
        reloadGrid && reloadGrid();
      }

      memberForm!.status = ClinicalFormsConstants.submittedStatus;
      setMemberForm(Object.assign({}, memberForm));
    } finally {
      dispatch(setLastAssessmentDate(formatDate(new Date())));
      LoadingHelper.hideLoader(".main");
    }
  }

  async function discardForm(): Promise<void> {
    LoadingHelper.showLoader(".main");
    try {
      if(memberForm?.status !== ClinicalFormsConstants.submittedStatus){
        await clinicalformsApi.clinicalFormsControllerUpdateMemberFormStatus(
          {
            value: ClinicalFormsConstants.discardedStatus,
          },
          memberForm!.id
        );
      }
      showDiscardConfirmation(false);
      onClosed?.();
      reloadGrid && reloadGrid();
    } finally {
      LoadingHelper.hideLoader(".main");
    }
  }

  const handleModalShow = (comp: string) => {
    offCanvasShowCallback && offCanvasShowCallback(comp);
  };

  async function submitPgis(): Promise<void> {
    LoadingHelper.showLoader(".main");
    try {
      const pgiRequests: PgiRequest[] = [];
      const addedGoalComments = new Set<string>();

      selectedProblems?.forEach((problem) => {
        if (!problem.goals?.length) return;

        problem.goals.forEach((goal) => {
          if (!goal.interventions?.length) return;

          goal.interventions.forEach((intervention) => {
            const isCommentAddedForGoal = addedGoalComments.has(goal.id);
            // Add the goal comment only for the first intervention of the goal
            const goalComment = !isCommentAddedForGoal
              ? `${memberForm?.title}`
              : "";
            if (!isCommentAddedForGoal) {
              addedGoalComments.add(goal.id);
            }
            pgiRequests.push({
              interventionId: intervention.id,
              goalId: goal.id,
              problemId: problem.id,
              clinicalFormVersionId: clinicalFormVersionId,
              goalDueDate:
                goal.defaultDueDays != null
                  ? (() => {
                      const currentDate = new Date();
                      const utcDate = new Date(
                        Date.UTC(
                          currentDate.getUTCFullYear(),
                          currentDate.getUTCMonth() + 1,
                          currentDate.getUTCDate()
                        )
                      );
                      utcDate.setUTCDate(
                        utcDate.getUTCDate() + goal.defaultDueDays
                      );
                      return utcDate;
                    })()
                  : (() => {
                      const currentDate = new Date();
                      return new Date(
                        Date.UTC(
                          currentDate.getUTCFullYear(),
                          currentDate.getUTCMonth() + 1,
                          currentDate.getUTCDate()
                        )
                      );
                    })(),
              goalDesignerId: "",
              interventionDesignerId: "",
              goalDesignerStateId: "",
              interventionDueDate:
                intervention.defaultDueDays != null
                  ? (() => {
                      const currentDate = new Date();
                      const utcDate = new Date(
                        Date.UTC(
                          currentDate.getUTCFullYear(),
                          currentDate.getUTCMonth() + 1,
                          currentDate.getUTCDate()
                        )
                      );
                      utcDate.setUTCDate(
                        utcDate.getUTCDate() + intervention.defaultDueDays
                      );
                      return utcDate;
                    })()
                  : (() => {
                      const currentDate = new Date();
                      return new Date(
                        Date.UTC(
                          currentDate.getUTCFullYear(),
                          currentDate.getUTCMonth() + 1,
                          currentDate.getUTCDate()
                        )
                      );
                    })(),
              assigneeUserId: user!.id,
              interventionDesignerStateId: "",
              problemDesignerId: "",
              problemDesignerStateId: "",
              goalComment,
              interventionComment: `${memberForm?.title}`,
              generatedByAssessment: true,
            });
          });
        });
      });

        try {
          await pgiApi.pgiControllerAddPgi(pgiRequests, patientId);
        } catch (error: any) {
          const errorMessage = error?.response?.data?.message?.message?.message;
          const toasterConfig: ToastConfig = {
            type: "danger",
            title: errorMessage,
            icon: "error_outline",
          };
          handleAddPgiForm(toasterConfig as ToastConfig);
        }
      onClosed?.();
      fromAddAssessment && navigate(`/patients/${patientId}/pgi`);
    } finally {
      LoadingHelper.hideLoader(".main");
    }
  }

  async function startAssessment(formId: string): Promise<void> {
    if (formId == memberForm?.formId) {
      return;
    }
    LoadingHelper.showLoader(".main");
    const request: AssignFormRequest = {
      formId: formId,
      source: source!,
      sourceRefId: memberForm?.sourceRefId ?? "",
    };
    try {
      const assessments =
        await clinicalformsApi.clinicalFormsControllerGetMemberForms(
          patientId,
          ClinicalFormsConstants.assessmentType
        );

      const existingDraftAssessment = assessments.data.filter(
        (assessment) =>
          assessment.formId === formId &&
          assessment.status === ClinicalFormsConstants.draftStatus
      );

      if (existingDraftAssessment.length > 0) {
        setMemberForm(existingDraftAssessment[0]);
        setAllAnswersGiven(false);
        setAnswers([]);

        LoadingHelper.hideLoader(".main");

        return;
      }

      const memberFormInfo: MemberFormInfo = (
        await clinicalformsApi.clinicalFormsControllerAssignClinicalForm(
          request,
          patientId
        )
      ).data;
      setMemberForm(memberFormInfo);
      setClinicalFormVersionId(memberFormInfo.id);
      setAllAnswersGiven(false);
      setAnswers([]);
    } finally {
      LoadingHelper.hideLoader(".main");
    }
  }

  function getScoreDiff(status: MemberFormStatus | undefined): string {
    if (!status?.currentScore || !status.lastScore) {
      return "";
    }
    if (status.currentScore == status.lastScore) {
      return "";
    }

    const diff = status.currentScore - status.lastScore;
    return " (" + (diff > 0 ? "+" : "") + diff + ")";
  }

  return (
    <>
      <div className="main assessment">
        <div className="assessment__aside ">
          <div className="text-light fs-11">ASSESSMENT</div>
          <h6>List of Assessments</h6>
          <div className="border rounded">
            <div className="table-responsive">
              <table className="table fs-12 m-0">
                <tbody>
                  {clinicalForms?.map((form, index) => (
                    <tr
                      key={form.id}
                      className={
                        form.id === memberForm?.formId
                          ? "member-forms-row member-forms-selected-row"
                          : "member-forms-row"
                      }
                      onClick={() => startAssessment(form.id)}
                    >
                      <td>{form.title}</td>
                      <td>
                        <TooltipWrapper
                          status={displayStatusMap.get(
                            assessmentStatus.get(form.id)?.status || ''
                          )!}
                          color={statusColorMap.get(
                            assessmentStatus.get(form.id)?.status || ''
                          )!}
                          dueDays={assessmentStatus.get(form.id)?.dueDays}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {memberForm &&
          memberForm.status != ClinicalFormsConstants.submittedStatus && (
            <ClinicalFormDetailsView
              formId={memberForm.formId}
              comments={comments}
              commentsPosition="right"
              readOnly={false}
              answers={answers}
              onAnswersChange={onAnswerChange}
              onAllAnswersGivenChange={setAllAnswersGiven}
              onCommentsChange={onCommentsChange}
              title={memberForm?.title}
              hideComments={true}
            >
              <div className="fs-13 mt-3">
                <div className="mb-2 row">
                  <div className="col-12 col-sm-6 col-lg-4 col-xl-3">
                    Last Value:{" "}
                    {formStatuses.get(memberForm.formId)?.currentScore}
                    {getScoreDiff(formStatuses.get(memberForm.formId))}
                  </div>
                  <div className="col-12 col-sm-6 col-lg-8 col-xl-9">
                    Last Completion Date:{" "}
                    {formatDate(
                      formStatuses.get(memberForm.formId)?.lastSubmittedOn
                    )}
                  </div>
                </div>
                <div className="row">
                  <div className="col-12 col-sm-6 col-lg-4 col-xl-3">
                    Status:{" "}
                    {formStatuses.get(memberForm.formId)?.isOverDue && (
                      <div className="d-inline-block alert alert-danger px-2 py-1">
                        Overdue
                      </div>
                    )}
                  </div>
                  <div className="col-12 col-sm-6 col-lg-8 col-xl-9">
                    Due Date:{" "}
                    {formatDate(
                      formStatuses.get(memberForm.formId)?.nextDueDate
                    )}
                  </div>
                </div>
              </div>
            </ClinicalFormDetailsView>
          )}
        {memberForm &&
          memberForm?.status === ClinicalFormsConstants.submittedStatus && (
            <div className="assessment__main">
              <div className="">
                <div className="text-light fs-11">MODULE</div>
                <h6>{memberForm?.title}</h6>
              </div>
              <AssessmentPGIs
                problems={selectedProblems!}
                onChange={setSelectedProblems}
                memberFormId={memberForm.id}
                disabledPgiIds={pgiIds}
              />
            </div>
          )}
        <div className="assessment__footer">
          <div className="text-light fs-12">
            {lastSaveTime && (
              <span>Draft auto saved at {formatDateTime(lastSaveTime)}</span>
            )}
            {memberForm?.status !== ClinicalFormsConstants.submittedStatus && (
              <>
                <button
                  className="btn btn-primary btn-sm rounded-pill ms-2 px-5"
                  disabled={!allAnswersGiven}
                  type="button"
                  onClick={submitForm}
                >
                  Submit
                </button>
                <button
                  className="btn btn-outline-primary btn-sm rounded-pill ms-2 px-5"
                  type="button"
                  onClick={() => {
                    onClosed && onClosed();
                    reloadGrid && reloadGrid();
                  }}
                >
                  Close
                </button>
              </>
            )}
            {memberForm?.status === ClinicalFormsConstants.submittedStatus && (
              <button
                className="btn btn-outline-primary btn-sm rounded-pill ms-2 px-5"
                // disabled={!allAnswersGiven}
                type="button"
                onClick={submitPgis}
              >
                Save PGIs
              </button>
            )}
            <button
              className="btn btn-outline-primary btn-sm rounded-pill ms-2 px-5"
              type="button"
              onClick={() => showDiscardConfirmation(true)}
            >
              Discard
            </button>
          </div>
        </div>
      </div>
      
      <Modal
        show={discardConfirmationVisible}
        onHide={() => showDiscardConfirmation(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Discard</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to discard the form?</Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => showDiscardConfirmation(false)}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={discardForm}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EditAssessmentView;